import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

import { translate } from '@/i18n'

import { useAuthStore } from '@/stores/auth'
import type { Permission } from '@/types/api'

declare module 'vue-router' {
  interface RouteMeta {
    /** Shown in the top bar (English fallback; titleKey wins). */
    title?: string
    /** The i18n key of the title. */
    titleKey?: string
    /** Reachable without a session (only the login screen). */
    public?: boolean
    /** Renders without the app shell. */
    bare?: boolean
    /** Required permission; the guard redirects to the dashboard without it. */
    permission?: Permission
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { title: 'Sign in', titleKey: 'nav.items.signin', public: true, bare: true },
  },
  { path: '/', redirect: { name: 'dashboard' } },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: { title: 'Operations dashboard', titleKey: 'nav.items.dashboard' },
  },
  {
    path: '/sites',
    name: 'sites',
    component: () => import('@/views/SitesView.vue'),
    meta: { title: 'Sites', titleKey: 'nav.items.sites', permission: 'sites.read' },
  },
  {
    path: '/cameras',
    name: 'cameras',
    component: () => import('@/views/CamerasView.vue'),
    meta: { title: 'Cameras', titleKey: 'nav.items.cameras' },
  },
  {
    path: '/cameras/:id',
    name: 'camera-detail',
    component: () => import('@/views/CameraDetailView.vue'),
    meta: { title: 'Camera', titleKey: 'nav.items.cameradetail' },
    props: true,
  },
  {
    // The camera is part of the URL so a zone layout is shareable with a colleague.
    path: '/zones/:cameraId?',
    name: 'zones',
    component: () => import('@/views/ZoneConfigurationView.vue'),
    meta: { title: 'Zone configuration', titleKey: 'nav.items.zoneconfiguration' },
    props: true,
  },
  {
    // Deliberately its own route rather than a tab on the dashboard: this is the screen
    // that goes on a monitor on a wall, and a wall display needs a URL of its own.
    path: '/live',
    name: 'live',
    component: () => import('@/views/LiveOperationsView.vue'),
    meta: { title: 'Live operations', titleKey: 'nav.items.liveoperations', permission: 'dashboard.read' },
  },
  {
    path: '/operations',
    name: 'operations',
    component: () => import('@/views/OperationsReportView.vue'),
    meta: { title: 'Operations report', titleKey: 'nav.items.operationsreport', permission: 'dashboard.read' },
  },
  {
    path: '/reports',
    name: 'reports',
    component: () => import('@/views/ReportsView.vue'),
    meta: { title: 'Reports', titleKey: 'nav.items.reports', permission: 'dashboard.read' },
  },
  {
    path: '/journeys',
    name: 'journeys',
    component: () => import('@/views/JourneysView.vue'),
    meta: { title: 'Customer journeys', titleKey: 'nav.items.customerjourneys', permission: 'dashboard.read' },
  },
  {
    path: '/quality',
    name: 'quality',
    component: () => import('@/views/QualityView.vue'),
    meta: { title: 'Quality', titleKey: 'nav.items.quality', permission: 'dashboard.read' },
  },
  {
    path: '/watches',
    name: 'watches',
    component: () => import('@/views/WatchesView.vue'),
    meta: { title: 'AI Watches', titleKey: 'nav.items.aiwatches', permission: 'dashboard.read' },
  },
  {
    path: '/sops',
    name: 'sops',
    component: () => import('@/views/WatchesView.vue'),
    meta: { title: 'SOPs', titleKey: 'nav.items.sops', permission: 'dashboard.read' },
    props: { mode: 'EXPECT' },
  },
  {
    path: '/library',
    name: 'use-case-library',
    component: () => import('@/views/UseCaseLibraryView.vue'),
    meta: { title: 'Use-case library', titleKey: 'nav.items.usecaselibrary', permission: 'dashboard.read' },
  },
  {
    path: '/kpis',
    name: 'kpis',
    component: () => import('@/views/KpiView.vue'),
    meta: { title: 'KPIs', titleKey: 'nav.items.kpis', permission: 'dashboard.read' },
  },
  {
    path: '/ai-manager',
    name: 'ai-manager',
    component: () => import('@/views/AiManagerView.vue'),
    meta: { title: 'AI Manager', titleKey: 'nav.items.aimanager', permission: 'dashboard.read' },
  },
  {
    path: '/reports/behaviour',
    name: 'behaviour-report',
    component: () => import('@/views/BehaviourReportView.vue'),
    meta: { title: 'Behaviour report', titleKey: 'nav.items.behaviourreport', permission: 'dashboard.read' },
  },
  {
    path: '/operations/service',
    name: 'service-report',
    component: () => import('@/views/ServiceReportView.vue'),
    meta: { title: 'Counter response', titleKey: 'nav.items.counterresponse', permission: 'dashboard.read' },
  },
  {
    path: '/events',
    name: 'events',
    component: () => import('@/views/EventTimelineView.vue'),
    meta: { title: 'Event timeline', titleKey: 'nav.items.eventtimeline' },
  },
  {
    path: '/review',
    name: 'review-queue',
    component: () => import('@/views/ReviewQueueView.vue'),
    meta: { title: 'Review queue', titleKey: 'nav.items.reviewqueue', permission: 'events.review' },
  },
  {
    path: '/alerts',
    name: 'alerts',
    component: () => import('@/views/AlertsView.vue'),
    meta: { title: 'Alerts', titleKey: 'nav.items.alerts', permission: 'events.read' },
  },
  {
    path: '/events/:id',
    name: 'event-detail',
    component: () => import('@/views/EventDetailView.vue'),
    meta: { title: 'Event', titleKey: 'nav.items.eventdetail' },
    props: true,
  },
  /*
   * Settings. Edge Nodes is readable by anyone who can see the operations console —
   * "why is nothing happening" is an operator question, not an administrator one — while
   * the actions inside it (pairing a node) are gated on `settings.manage`. The AI
   * provider screen is gated at the route, because `docs/api-poc-additions.md` §6 puts
   * `settings.manage` on all three of its endpoints and there is nothing to show without
   * them.
   */
  {
    path: '/settings/edge-nodes',
    name: 'edge-nodes',
    component: () => import('@/views/EdgeNodesView.vue'),
    meta: { title: 'Edge nodes', titleKey: 'nav.items.edgenodes' },
  },
  {
    path: '/settings/users',
    name: 'users',
    component: () => import('@/views/UsersView.vue'),
    meta: { title: 'Users', titleKey: 'nav.items.users', permission: 'users.manage' },
  },
  {
    path: '/people/employees',
    name: 'employees',
    component: () => import('@/views/EmployeesView.vue'),
    meta: { title: 'Employees', titleKey: 'nav.items.employees', permission: 'settings.manage' },
  },
  {
    path: '/settings/integrations',
    name: 'integrations',
    component: () => import('@/views/IntegrationsView.vue'),
    meta: { title: 'Integrations', titleKey: 'nav.items.integrations', permission: 'settings.manage' },
  },
  {
    path: '/settings/customers',
    name: 'customers',
    component: () => import('@/views/CustomersView.vue'),
    meta: { title: 'Customers', titleKey: 'nav.items.customers', permission: 'platform.manage' },
  },
  {
    path: '/settings/ai-providers',
    name: 'ai-providers',
    component: () => import('@/views/AiProviderSettingsView.vue'),
    meta: { title: 'AI providers', titleKey: 'nav.items.aiproviders', permission: 'settings.manage' },
  },
  {
    path: '/account',
    name: 'account',
    component: () => import('@/views/AccountView.vue'),
    meta: { title: 'Account', titleKey: 'nav.items.account' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { title: 'Not found', titleKey: 'nav.items.notfound' },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, saved) {
    return saved ?? { top: 0 }
  },
})

router.beforeEach((to) => {
  const auth = useAuthStore()

  if (to.meta.public) {
    // Already signed in? Skip the login screen.
    if (to.name === 'login' && auth.isAuthenticated) return { name: 'dashboard' }
    return true
  }

  if (!auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  const required = to.meta.permission
  if (required && !auth.hasPermission(required)) {
    // Deliberately not a silent 403: the dashboard shows a toast explaining why.
    return { name: 'dashboard', query: { denied: required } }
  }

  return true
})

router.afterEach((to) => {
  const key = to.meta.titleKey as string | undefined
  const title = key ? translate(key) : (to.meta.title as string | undefined)
  document.title = title ? `${title} · U-Observe AI` : 'U-Observe AI'
})

export default router
